import React, { FC, useEffect, useState } from "react";
import { useGetAlternativesExercizesMutation, useReplaceExercizeOnTrainingMutation } from "@/store/trainingsApi";
import { tg } from "@/constants/telegram";
import styles from "./ExerciseAlternativesModal.module.scss";

type Exercise = {
  id: number;
  title: string;
  title_photo: string;
  description?: string;
  muscle_group?: string;
  documentation?: string;
};

type ExerciseAlternativesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  exerciseId: number;
  trainingId: number;
  onExerciseReplaced: () => void;
};

export const ExerciseAlternativesModal: FC<ExerciseAlternativesModalProps> = ({
  isOpen,
  onClose,
  exerciseId,
  trainingId,
  onExerciseReplaced,
}) => {
  const [alternatives, setAlternatives] = useState<Exercise[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  
  const [getAlternativesExercizes] = useGetAlternativesExercizesMutation();
  const [replaceExercizeOnTraining] = useReplaceExercizeOnTrainingMutation();

  useEffect(() => {
    if (isOpen && exerciseId) {
      fetchAlternatives();
    }
  }, [isOpen, exerciseId]);

  const fetchAlternatives = async () => {
    if (!tg?.initData) return;
    
    setIsLoading(true);
    try {
      const response = await getAlternativesExercizes({
        exercize_id: exerciseId,
      }).unwrap();
      
      setAlternatives(response.alternatives || []);
    } catch (error) {
      console.error("Error fetching alternatives:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplaceExercise = async (newExerciseId: number) => {
    if (!tg?.initData) return;
    
    setIsReplacing(true);
    try {
      await replaceExercizeOnTraining({
        training_id: trainingId,
        old_exercize_id: exerciseId,
        new_exercize_id: newExerciseId,
      }).unwrap();
      
      onExerciseReplaced();
      onClose();
    } catch (error) {
      console.error("Error replacing exercise:", error);
    } finally {
      setIsReplacing(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Выберите альтернативное упражнение</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Загружаем альтернативные упражнения...</p>
            </div>
          ) : alternatives.length === 0 ? (
            <div className={styles.empty}>
              <p>Альтернативные упражнения не найдены</p>
            </div>
          ) : (
            <div className={styles.exercisesList}>
              {alternatives.map((exercise) => (
                <div key={exercise.id} className={styles.exerciseItem}>
                  <div className={styles.exerciseImage}>
                    <img src={exercise.title_photo} alt={exercise.title} />
                  </div>
                  <div className={styles.exerciseInfo}>
                    <h3 className={styles.exerciseTitle}>{exercise.title}</h3>
                    {exercise.muscle_group && (
                      <div className={styles.muscleGroup}>
                        {exercise.muscle_group}
                      </div>
                    )}
                    {exercise.documentation && (
                      <p className={styles.exerciseDescription}>
                        {exercise.documentation}
                      </p>
                    )}
                  </div>
                  <button
                    className={styles.replaceButton}
                    onClick={() => handleReplaceExercise(exercise.id)}
                    disabled={isReplacing}
                  >
                    {isReplacing ? "Заменяем..." : "Заменить"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

